import { CSSProperties, FC } from 'react';

import { isEmpty } from 'lodash/fp';

import { styled } from '../../theme';
import FlexColumn from '../FlexColumn';
import Tooltip from '../Tooltip';
import TruncatedTextWithTooltip from '../TruncatedTextWithTooltip';
import { Chip } from './styles';

const TOOLTIP_MAX_CONTENT_HEIGHT = 300;

const INLINE_TOOLTIP_WRAPPER_STYLES: CSSProperties = {
    display: 'flex',
    flexShrink: 0,
    minWidth: 0,
};

const ChipLabel = styled.span(({ theme }) => ({
    fontWeight: theme.typography.fontWeight.heavy,
}));

const MoreCounter = styled.span(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    padding: '0 8px',
    borderRadius: theme.borderRadius.base,
    background: theme.color.white,
    fontSize: 14,
    lineHeight: '20px',
    color: theme.color.jetBlack,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    wordBreak: 'normal',
}));

const TooltipValuesList = styled(FlexColumn)({
    gap: 8,
});

const TooltipValue = styled.div(({ theme }) => ({
    fontSize: 14,
    lineHeight: '20px',
    color: theme.color.jetBlack,
    whiteSpace: 'normal',
    overflowWrap: 'normal',
}));

export type FilterChipVariant = 'default' | 'inline';

export interface FilterChipProps {
    label: string;
    firstValue: string;
    remainingValues: string[];
    moreLabel: string;
    animationDelay?: number;
    variant?: FilterChipVariant;
}

const FilterChip: FC<FilterChipProps> = ({
    label,
    firstValue,
    remainingValues,
    moreLabel,
    animationDelay = 0,
    variant = 'default',
}) => {
    const hasRemainingValues = !isEmpty(remainingValues);

    if (variant === 'inline') {
        return (
            <Tooltip
                title={
                    <TooltipValuesList>
                        <ChipLabel>{label}:</ChipLabel>
                        {[firstValue, ...remainingValues].map((value, valueIndex) => (
                            <TooltipValue key={valueIndex}>{value}</TooltipValue>
                        ))}
                    </TooltipValuesList>
                }
                placement="bottom"
                trigger="hover"
                maxContentHeight={TOOLTIP_MAX_CONTENT_HEIGHT}
                wrapperStyles={INLINE_TOOLTIP_WRAPPER_STYLES}
            >
                <Chip animationDelay={animationDelay} isInline>
                    <TruncatedTextWithTooltip
                        text={firstValue}
                        shouldSuppressTooltip
                        wrapperStyles={{ width: 'auto' }}
                    />
                    {hasRemainingValues && <MoreCounter>{moreLabel}</MoreCounter>}
                </Chip>
            </Tooltip>
        );
    }

    return (
        <Chip animationDelay={animationDelay}>
            <span>
                <ChipLabel>{label}: </ChipLabel>
                {firstValue}
            </span>
            {hasRemainingValues && (
                <Tooltip
                    title={
                        <TooltipValuesList>
                            {remainingValues.map((value, valueIndex) => (
                                <TooltipValue key={valueIndex}>{value}</TooltipValue>
                            ))}
                        </TooltipValuesList>
                    }
                    placement="bottom"
                    trigger="hover"
                    maxContentHeight={TOOLTIP_MAX_CONTENT_HEIGHT}
                >
                    <MoreCounter>{moreLabel}</MoreCounter>
                </Tooltip>
            )}
        </Chip>
    );
};

export default FilterChip;
